import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import { CareerChallengeListItemResponse } from '../../../../models';

@Component({
  selector: 'app-career-challenge-list',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSnackBarModule
  ],
  templateUrl: './career-challenge-list.component.html',
  styleUrl: './career-challenge-list.component.scss'
})
export class CareerChallengeListComponent implements OnInit {
  private readonly api = inject(CsrWebApiService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  loading = false;
  copyingId: string | null = null;
  deletingId: string | null = null;
  items: CareerChallengeListItemResponse[] = [];
  totalCount = 0;
  readonly columns = ['title', 'type', 'status', 'slug', 'website', 'participants', 'actions'];
  readonly statuses = ['draft', 'published', 'registration', 'active', 'judging', 'completed', 'archived'];

  readonly filterForm = this.fb.nonNullable.group({
    page: [1, [Validators.required, Validators.min(1)]],
    pageSize: [20, [Validators.required, Validators.min(1), Validators.max(100)]],
    status: [''],
    searchText: ['']
  });

  ngOnInit(): void {
    void this.load();
  }

  async load(): Promise<void> {
    if (this.filterForm.invalid) {
      return;
    }
    this.loading = true;
    try {
      const raw = this.filterForm.getRawValue();
      const response = await firstValueFrom(
        this.api.listAdminCareerChallenges(
          raw.page,
          raw.pageSize,
          raw.status || undefined,
          raw.searchText.trim() || undefined
        )
      );
      this.items = response.data ?? [];
      this.totalCount = response.count ?? 0;
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.loading = false;
    }
  }

  onPage(event: PageEvent): void {
    this.filterForm.patchValue({ page: event.pageIndex + 1, pageSize: event.pageSize });
    void this.load();
  }

  open(id: string): void {
    void this.router.navigate(['/admin/career-challenges', id]);
  }

  async copy(row: CareerChallengeListItemResponse): Promise<void> {
    this.copyingId = row.id;
    try {
      const created = await firstValueFrom(
        this.api.copyCareerChallenge(row.id, { title: `Copy of ${row.title}` })
      );
      this.snackBar.open('Draft copy created. Set the schedule, then publish.', 'Close', { duration: 4000 });
      void this.router.navigate(['/admin/career-challenges', created.id, 'overview']);
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.copyingId = null;
    }
  }

  async deleteDraft(row: CareerChallengeListItemResponse): Promise<void> {
    if (row.status !== 'draft') {
      return;
    }
    if (!confirm(`Delete draft challenge "${row.title}"? This cannot be undone.`)) {
      return;
    }
    this.deletingId = row.id;
    try {
      await firstValueFrom(this.api.deleteCareerChallenge(row.id));
      this.snackBar.open('Draft challenge deleted.', 'Close', { duration: 3000 });
      await this.load();
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.deletingId = null;
    }
  }

  startCreate(): void {
    void this.router.navigate(['/admin/career-challenges', 'new', 'overview']);
  }

  private readError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const detail = error.error?.detail ?? error.error?.title ?? error.error;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
    }
    return 'Request failed';
  }

}
